import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Tag, Loader2, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { formatCurrency } from "@/lib/format";

export default function CustomerDiscountModal({
  open,
  onOpenChange,
  customerId: propCustomerId,
  customerName: propCustomerName,
  maxAvailable: propMaxAvailable,
  customer,
  onSuccess,
}) {
  const customerId = propCustomerId || customer?.id;
  const customerName =
    propCustomerName ||
    customer?.display_name ||
    customer?.company_name ||
    "Müşteri";

  const fin = customer?.financial_summary;
  const fallbackMax = fin
    ? Math.max(
        0,
        parseFloat(
          fin.net_balance !== undefined
            ? fin.net_balance
            : fin.net_outstanding !== undefined
            ? fin.net_outstanding
            : fin.outstanding_receivables || 0
        )
      )
    : 0;

  const maxVal = Math.max(
    0,
    parseFloat(propMaxAvailable !== undefined ? propMaxAvailable : fallbackMax)
  );

  const [amount, setAmount] = useState("");
  const [discountDate, setDiscountDate] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount("");
      setDescription("");
      // Default to today in YYYY-MM-DD
      const today = new Date().toISOString().split("T")[0];
      setDiscountDate(today);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);

    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error("Lütfen sıfırdan büyük geçerli bir iskonto tutarı girin.");
      return;
    }

    if (numAmount > maxVal) {
      toast.error(
        `İskonto tutarı (${formatCurrency(numAmount)}), müşterinin kalan açık alacağını (${formatCurrency(maxVal)}) aşamaz.`
      );
      return;
    }

    if (!description.trim()) {
      toast.error("Lütfen iskonto için bir açıklama girin.");
      return;
    }

    if (!customerId) {
      toast.error("Müşteri bilgisi bulunamadı.");
      return;
    }

    setSubmitting(true);
    try {
      const isoDate = discountDate
        ? new Date(`${discountDate}T12:00:00`).toISOString()
        : new Date().toISOString();

      await api.post(`/customers/${customerId}/discounts`, {
        amount: numAmount,
        discount_date: isoDate,
        description: description.trim(),
      });

      toast.success("Müşteri iskontosu başarıyla uygulandı.");
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(formatApiError(err, "İskonto uygulanamadı."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white p-6">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Tag className="text-purple-600" size={20} />
            <DialogTitle className="text-lg font-bold text-[#171A1F]">
              Müşteri İskontosu Uygula
            </DialogTitle>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {customerName} cari hesabına genel iskonto tanımlayın. Bu işlem iş sözleşme bedellerini değiştirmez, cari kalan alacağı düşürür.
          </p>
        </DialogHeader>

        {maxVal <= 0 ? (
          <div
            className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2.5 my-2"
            data-testid="discount-no-receivable-notice"
          >
            <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <strong className="block font-semibold">Açık Alacak Bulunmuyor</strong>
              Bu müşterinin ödenecek kalan açık borcu bulunmadığından yeni bir iskonto uygulanamaz.
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="p-3 bg-purple-50/70 border border-purple-100 rounded-md text-xs text-purple-900 flex justify-between items-center">
              <span>Uygulanabilir Maksimum İskonto:</span>
              <span
                className="font-mono font-bold text-sm text-purple-950"
                data-testid="discount-max-available"
              >
                {formatCurrency(maxVal)}
              </span>
            </div>

            {/* Field 1: Amount */}
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">
                İskonto Tutarı (TL) *
              </label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={maxVal}
                required
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="border-[#DCE2E8] font-mono"
                data-testid="input-discount-amount"
              />
            </div>

            {/* Field 2: Date */}
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">
                İskonto Tarihi *
              </label>
              <Input
                type="date"
                required
                value={discountDate}
                onChange={(e) => setDiscountDate(e.target.value)}
                className="border-[#DCE2E8]"
                data-testid="input-discount-date"
              />
            </div>

            {/* Field 3: Description */}
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">
                Açıklama / İskonto Nedeni *
              </label>
              <Textarea
                rows={2}
                required
                placeholder="Örn: Müşteri sadakat indirimi / pazarlık mutabakatı"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="border-[#DCE2E8] text-xs"
                data-testid="input-discount-description"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-[#DCE2E8]"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="btn-save-discount"
              >
                {submitting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                İskontoyu Kaydet
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
